import React, { useState, useRef, useEffect } from "react";
import RouteData from "../../../interfaces/routes/routeData";

interface IProps {
	onRouteChange: (data: RouteData) => void,
	selectedRoute: string,
}
function RouteSelector({onRouteChange, selectedRoute}: IProps) {
	const environment = process.env.NODE_ENV;
	const api = "/webapi/atis/allroutes";
	const [expanded, updateExpandedStatus] = useState(false);
	const [routes, updateRoutes] = useState<RouteData[]>([]);
	const [activeRoute, updateActiveRoute] = useState<RouteData>();
	const [searchTerm, updateSearchTerm] = useState('');
	const toggleRef = useRef<HTMLButtonElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const toggleDropdown = () => {
		updateExpandedStatus(!expanded);
		updateSearchTerm('');
	}
	
	// selecting a route in the dropdown
	const selectRoute = (route: any) => {
		updateActiveRoute(route);
		updateExpandedStatus(false);
		onRouteChange(route);
	}
	
	// searching for a keyword in the dropdown
	const handleRouteSearch = (e: React.SyntheticEvent) => {
		const target = e.target as HTMLInputElement;
		const value = target.value;
		updateSearchTerm(value);
	}

	// get a list of all routes
	const getRouteData = () => {
		// demo data to test locally if you cant connect to the vpn
		const url = window.location.origin;
		if (environment === 'development' || url === 'https://www.capmetro-local.com') {
			const allRoutesJson = require('../demoData/allroutes.json');
			updateRoutes(allRoutesJson.Data);
		} else {
			fetch(api)
				.then((response) => {
					if (response.ok) {
						return response.json();
					}
				})
				.then((data) => {
					updateRoutes(data.Data);
				})
				.catch((error) => {
					console.error("Error retrieving the atis routes", error);
				});
		}
	}

	useEffect(() => {
		if (expanded) {
			// without the delay, the list scrolls a bit before focus
			setTimeout(() => {
				inputRef.current?.focus();
			},100);
		}
	},[expanded])

	// routes can be selected from "rail service" button
	useEffect(() => {
		if (selectedRoute && routes) {
			if (selectedRoute !== "all") {
				const routeNumber = Number(selectedRoute);
				const filteredRoutes = routes.filter((x) => x.RouteNumber === routeNumber);
				if (filteredRoutes.length > 0) {
					selectRoute(filteredRoutes[0]);
				}
			} else {
				updateExpandedStatus(true);
			}
		}
	},[selectedRoute])

	useEffect(() => {
		// load route if query string value is found
		const params = new URLSearchParams(window.location.search);
		const route = params.get('route');
		if (route && routes) {
			routes.forEach((item) => {
				if (item.RouteNumber === Number(route)) {
					selectRoute(item);
				}
			});
			if (route === 'all') {
				updateExpandedStatus(true);
			}
		}
	},[routes]);

	useEffect(() => {
		getRouteData();
	},[]);

	if (routes) {
		return (
			<div className="route-selector">
				<button type="button" ref={toggleRef} aria-expanded={expanded} className="route-selector__toggle" onClick={toggleDropdown} aria-label={`select a route from the dropdown. ${activeRoute ? `currently selected ${activeRoute.DisplayName}` : ''}`}>
					{
						activeRoute
							?
							<span>{activeRoute.DisplayName}</span>
							: "e.g: Express, Rail, Rapid, Duval..."
					}
				</button>
				<div className={`route-selector__dropdown ${expanded ? 'active' : ''}`}>
					<label className="route-selector__label">
						<input type="text" ref={inputRef} className="route-selector__search" onChange={handleRouteSearch} value={searchTerm}/>
						<span className="visually-hidden">Enter a keyword to search for a route.</span>
					</label>
					<span className="visually-hidden" aria-live="polite">
						{
							routes.filter((item) => item.DisplayName.toUpperCase().indexOf(searchTerm.toUpperCase()) > - 1).length > 0
							?
							`found ${routes.filter((item) => item.DisplayName.toUpperCase().indexOf(searchTerm.toUpperCase()) > - 1).length} results`
							: 'No results found'
						}
					</span>
					<ul className="route-selector__list">
						{
							routes.filter((item) => item.DisplayName.toUpperCase().indexOf(searchTerm.toUpperCase()) > - 1).map((route) => {
								return (
									<li key={route.RouteNumber} className="route-selector__item">
										<button type="button" className={`route-selector__option ${activeRoute?.RouteNumber === route.RouteNumber ? 'active' : ''}`} data-route={route.RouteNumber} onClick={() => selectRoute(route)}>{route.DisplayName}</button>
									</li>
								)
							})
						}
					</ul>
				</div>
			</div>
		)
	} else {
		return null;
	}
}
export default RouteSelector;